import { useTheme } from '@emotion/react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import { ThemeProvider } from '@mui/material/styles'
import muiTheme from '../../themes/muiTheme'

// GuidedChoice brand blue, used only when the white-label theme is missing.
const FALLBACK_FILL = '#038ab7'
const FALLBACK_TRACK = '#e6e9ea'

/**
 * Mobile-responsive step-by-step advice progress indicator.
 *
 * Renders one "dash" per step. Dashes up
 * to and including the current step are filled with the client's brand color.
 * Tapping a dash navigates to that step via `onStepChange`.
 */
const MobileProgressSteps = props => {
  const { totalSteps, currentStep, onStepChange, stepLabels, fillColor, trackColor, className } =
    props
  const theme = useTheme()

  const resolvedFill = fillColor || theme.primaryColor || FALLBACK_FILL
  const resolvedTrack = trackColor || theme.homeActionButtonBgColor || FALLBACK_TRACK

  return (
    <ThemeProvider theme={muiTheme}>
      <Box
        className={className}
        role='group'
        aria-label='Advice progress'
        sx={{ display: 'flex', alignItems: 'center', columnGap: '5px', width: '100%' }}>
        {Array.from({ length: totalSteps }, (_, i) => {
          const step = i + 1
          const isFilled = step <= currentStep
          const isCurrent = step === currentStep
          const label = stepLabels?.[i]
          return (
            // The button is the tap target, so it spans a taller strip than the
            // visible dash to enlarge the touch area while the dash stays slim.
            <ButtonBase
              key={step}
              onClick={() => onStepChange?.(step)}
              disabled={!onStepChange}
              aria-label={label ? `Go to ${label}` : `Go to step ${step}`}
              aria-current={isCurrent ? 'step' : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
              }}>
              <Box
                aria-hidden
                sx={{
                  width: '100%',
                  height: '3px',
                  borderRadius: '4px',
                  backgroundColor: isFilled ? resolvedFill : resolvedTrack,
                  transition: 'background-color 0.3s ease',
                }}
              />
            </ButtonBase>
          )
        })}
      </Box>
    </ThemeProvider>
  )
}

MobileProgressSteps.propTypes = {
  totalSteps: PropTypes.number.isRequired,
  currentStep: PropTypes.number.isRequired,
  onStepChange: PropTypes.func,
  stepLabels: PropTypes.arrayOf(PropTypes.string),
  fillColor: PropTypes.string,
  trackColor: PropTypes.string,
  className: PropTypes.string,
}

export default MobileProgressSteps
